/**
 * 주소 안의 비밀 조각 가리기 — «주소 안의 비밀 조각(16자 이상 무작위 문자열)은 로그·행동 기록·Sentry 어디에도
 * 원문으로 남기지 않는다»(2026-10-06, 공통 기록 규격 §1-4 · workspace ops/workflow/log-standard.md).
 *
 * 공개 창구(고객 입력폼·서명 패드·견적·페이백·상담 …)는 로그인 대신 **주소 안의 토큰**으로 문을 연다. 그 주소가
 * 서버 접속 줄·행동 기록(S3 영구 백업)·Sentry 이벤트에 원문으로 남으면, 그걸 볼 수 있는 사람은 누구나 그 문을 연다.
 * 그래서 남기기 전에 **경로 틀**(`/form/:token`)로 바꾼다.
 *
 * 두 겹이다.
 *  1) 알려진 토큰 화면 — 첫 조각이 아래 목록이면 그다음 조각은 길이와 상관없이 `:token`.
 *  2) 그 밖 — 16자 이상이면서 숫자나 대문자가 섞인 조각은 `:token`(목록을 빠뜨린 창구 대비). UUID·숫자는 `:id`.
 *     `dealer-invoice-confirmations` 같은 긴 경로 이름(소문자 낱말)은 그대로 둔다 — 어느 화면인지 계속 보여야 한다.
 *
 * 화면(src·leads)·서버·행동 기록 백업이 같은 함수를 쓴다. 매장 사이트(storefront/app.js)는 빌드가 없어 같은 규칙을 옮겨 적었다.
 */

/** 화면 주소에서 «이 조각 다음이 토큰» 인 첫 조각(src/App.tsx 공개 라우트). */
export const TOKEN_SCREENS = new Set([
  "form",
  "form-simple",
  "signature-pad",
  "policy-upload",
  "payback",
  "payback-apply",
  "quote",
  "rebate",
  "f",
  "talk",
]);

/** 서버 주소에서 «이 접두어 다음 조각이 토큰»(lib/log-url-redact.ts 의 TOKEN_AFTER 와 같은 목록 + public·media). */
const TOKEN_AFTER_PREFIX = [
  "/api/v2/public-forms",
  "/api/v2/public-signature-pad",
  "/api/v2/public-quote",
  "/api/v2/public-payback",
  "/api/v2/public-payback-apply",
  "/api/v2/public-rebate",
  "/api/v2/public-sms-media",
  "/api/v2/talk",
  "/api/v2/custom-forms/public",
  "/api/v2/sales-leads/public",
  "/api/v2/storefront/media",
].map((p) => p.split("/"));

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** 무작위 값으로 보이는 조각 — 16자 이상, 주소에 쓰이는 글자만, 숫자나 대문자가 섞였다. */
export function looksSecret(segment: string): boolean {
  if (segment.length < 16) return false;
  if (!/^[A-Za-z0-9_\-.~%]+$/.test(segment)) return false;
  return /\d/.test(segment) || /[A-Z]/.test(segment);
}

/** 경로(질의문자열 없는)의 비밀 조각을 `:token`, UUID·숫자를 `:id` 로 바꾼다. */
export function maskSecretPath(path: string): string {
  const segs = path.split("/");
  const tokenAt = new Set<number>();
  // 화면 주소 `/form/<토큰>` — 첫 조각(segs[1])이 토큰 화면이면 segs[2] 가 토큰.
  if (segs.length > 2 && TOKEN_SCREENS.has(segs[1])) tokenAt.add(2);
  for (const prefix of TOKEN_AFTER_PREFIX) {
    if (segs.length > prefix.length && prefix.every((part, i) => segs[i] === part)) tokenAt.add(prefix.length);
  }
  return segs
    .map((seg, i) => {
      if (!seg) return seg;
      if (tokenAt.has(i)) return ":token";
      if (/^\d+$/.test(seg) || UUID_RE.test(seg)) return ":id";
      if (looksSecret(seg)) return ":token";
      return seg;
    })
    .join("/");
}

/**
 * 주소 전체(절대·상대) → 질의문자열·해시를 버리고 경로를 가린다. Sentry 이벤트의 url·breadcrumb 용.
 * 질의문자열에는 서명된 파일 링크·검색어가 실리므로 이름도 남기지 않는다.
 */
export function maskSecretUrl(url: string): string {
  if (!url) return url;
  const m = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]*)?([^?#]*)/i.exec(url);
  if (!m) return url;
  return `${m[1] ?? ""}${maskSecretPath(m[2] ?? "")}`;
}

/** Sentry 이벤트(화면·서버 공용 모양)에서 주소가 실리는 자리를 가린다. 그 자리 그대로 고쳐 돌려준다. */
export function maskSentryEvent<
  E extends {
    request?: { url?: string; query_string?: unknown; headers?: Record<string, string> };
    transaction?: string;
    breadcrumbs?: Array<{ data?: Record<string, unknown> | null; message?: string }>;
    tags?: Record<string, unknown>;
  },
>(event: E): E {
  if (event.request) {
    if (event.request.url) event.request.url = maskSecretUrl(event.request.url);
    if (event.request.query_string) event.request.query_string = undefined;
    if (event.request.headers) {
      const h = event.request.headers;
      for (const k of Object.keys(h)) {
        const lk = k.toLowerCase();
        if (lk === "referer" || lk === "referrer") h[k] = maskSecretUrl(h[k]);
        if (lk === "x-monitoring-token" || lk === "authorization" || lk === "cookie") delete h[k];
      }
    }
  }
  if (event.transaction) event.transaction = maskSecretUrl(event.transaction);
  if (event.tags && typeof event.tags.url === "string") event.tags.url = maskSecretUrl(event.tags.url);
  for (const b of event.breadcrumbs ?? []) maskSentryBreadcrumb(b);
  return event;
}

/** breadcrumb(화면 이동 from/to · fetch/xhr url) 의 주소를 가린다. */
export function maskSentryBreadcrumb<B extends { data?: Record<string, unknown> | null; message?: string }>(b: B): B {
  if (b.data) {
    for (const k of ["url", "from", "to"]) {
      const v = b.data[k];
      if (typeof v === "string") b.data[k] = maskSecretUrl(v);
    }
  }
  return b;
}
